import { apiFetch } from '@/lib/http';
import { EXPLAIN_PROMPT, pickLlmText } from './llm';
import type {
  AgentActionDetail,
  AgentActionFeedResponse,
  AgentActionReportResponse,
  DependencyMapData,
  NotificationsResponse,
  ReportFocus,
  StatusFilter,
  TimeRange,
} from './types';

function sinceFromRange(range: TimeRange): string {
  const hours = range === '7d' ? 24 * 7 : range === '30d' ? 24 * 30 : 24;
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

export async function fetchAgentActionFeed(params: {
  app_id?: string | null;
  since?: string;
  range?: TimeRange;
  limit?: number;
  cursor?: string | null;
}): Promise<AgentActionFeedResponse> {
  const qs = new URLSearchParams();
  if (params.app_id) qs.set('app_id', params.app_id);
  qs.set('since', params.since || sinceFromRange(params.range || '24h'));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.cursor) qs.set('cursor', params.cursor);
  return apiFetch(`/agent-actions/feed?${qs.toString()}`);
}

export async function fetchAgentActionDetail(id: string): Promise<AgentActionDetail> {
  return apiFetch(`/agent-actions/${encodeURIComponent(id)}`);
}

export async function fetchAgentDependencies(appId: string): Promise<DependencyMapData> {
  const qs = new URLSearchParams({ app_id: appId });
  return apiFetch(`/agent-actions/dependencies?${qs.toString()}`);
}

export async function fetchNotifications(appId?: string | null): Promise<NotificationsResponse> {
  const qs = appId ? `?app_id=${encodeURIComponent(appId)}` : '';
  return apiFetch(`/notifications${qs}`);
}

export async function dismissNotification(id: string): Promise<{ success: boolean }> {
  return apiFetch(`/notifications/${encodeURIComponent(id)}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function fetchAgentActionReport(params: {
  app_id?: string | null;
  since?: string;
  until?: string;
  focus?: ReportFocus;
  status_filter?: StatusFilter;
  regenerate?: boolean;
}): Promise<AgentActionReportResponse> {
  return apiFetch('/agent-actions/report', {
    method: 'POST',
    body: JSON.stringify({
      app_id: params.app_id || undefined,
      since: params.since,
      until: params.until,
      focus: params.focus || 'all',
      status_filter: params.status_filter && params.status_filter !== 'all'
        ? params.status_filter
        : undefined,
      regenerate: Boolean(params.regenerate),
    }),
  });
}

export async function explainAgentAction(detail: AgentActionDetail): Promise<string> {
  const resp = await apiFetch<unknown>('/volcano/execute', {
    method: 'POST',
    body: JSON.stringify({
      agent_definition: {
        id: 'agent-action-explainer',
        type: 'volcano',
        volcano_workflow: {
          steps: [
            {
              name: 'explain',
              provider: 'anthropic',
              model: 'claude-haiku-4-5-20251001',
              prompt: `${EXPLAIN_PROMPT}\n\nACTION_JSON:\n{{input}}`,
              input: '{{input}}',
              timeout: 60000,
            },
          ],
        },
      },
      input: JSON.stringify({
        id: detail.id,
        type: detail.type,
        title: detail.title,
        app_id: detail.app_id,
        app_name: detail.app_name,
        trigger: detail.trigger,
        timeline: detail.timeline,
        result: detail.result,
        metrics: detail.metrics,
        timestamp: detail.timestamp,
      }),
    }),
  });
  return pickLlmText(resp);
}
